import React, { useLayoutEffect, useCallback, useEffect } from 'react';
import { View, StyleSheet } from 'react-native';
import Animated from 'react-native-reanimated';
import { AppView } from 'design-system/AppView';
import Button, { HEIGHT } from 'design-system/Button';
import { ButtonGroup } from 'design-system/ButtonGroup';
import { rem, SCREEN_BOTTOM_PADDING, SCROLL_INDICATOR_INSETS, } from 'design-system/values';
import withAnimatedNavigationHeader from 'design-system/hoc/withAnimatedNavigationHeader';
import { useMarginBottom } from 'hooks/useMarginBottom';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import { getIncomes } from 'actions';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import ScreenInfoHeader from 'design-system/ScreenInfoHeader';
import HelpScreenButton from 'features/help/components/HelpScreenButton';
import { HELP_ARTICLES_KEY } from 'features/help/constants';
import { isWeb } from '../../../constants';
import SetUpBudgetsContentHeader from '../components/SetUpBudgetsContentHeader';
const strings = {
    title: `Let’s set budgets for total spend and each category`,
    description: 'Emma can suggest a budget for you, or you can create one from scratch',
};
export const SetUpBudgetScreenContent = ({ navigation, scrollHandler, route, }) => {
    const isInTab = route.name === 'BudgetingTab';
    const { paddingHorizontalStyle } = useAppFrameDimensions(isWeb || isInTab);
    const newPeriod = route.params && 'newPeriod' in route.params
        ? route.params.newPeriod
        : undefined;
    const keyBack = route.params && 'keyBack' in route.params
        ? route.params.keyBack
        : undefined;
    const dispatch = useAppDispatch();
    const didSetPeriod = useAppSelector((state) => state.deviceSettings.didSetPeriod);
    useEffect(() => {
        dispatch(getIncomes());
    }, []);
    useLayoutEffect(() => {
        navigation.setOptions({
            headerRight: () => (<HelpScreenButton articlesKey={HELP_ARTICLES_KEY.BUDGETING_ONBOARDING}/>),
        });
    }, []);
    const navigateToSetBudget = useCallback(() => {
        if (newPeriod) {
            navigation.navigateDeprecated('EditTotalBudget', {
                flow: 'Set',
                keyBack,
                period: newPeriod,
                hasPrefilledBudgets: true,
            });
        }
        else if (didSetPeriod) {
            navigation.navigateDeprecated('EditBudget', {
                flow: 'Set',
            });
        }
        else {
            navigation.navigateDeprecated('SelectPeriod', {
                period: undefined,
                monthlyBudget: undefined,
                budgetState: undefined,
                flow: 'Set',
                keyBack,
                createOwnBudget: true,
            });
        }
    }, [didSetPeriod, keyBack, newPeriod]);
    const navigateToPeriod = useCallback(() => {
        if (newPeriod) {
            navigation.navigateDeprecated('ProcessAutoSetUpBudget', {
                flow: 'Set',
                period: newPeriod,
                resetKey: Date.now(),
            });
        }
        else {
            navigation.navigateDeprecated('SelectPeriod', {
                period: undefined,
                monthlyBudget: undefined,
                budgetState: undefined,
                flow: 'Set',
                keyBack,
            });
        }
    }, [keyBack, newPeriod]);
    const onPressOwn = useCallback(async () => {
        navigateToSetBudget();
    }, [navigateToSetBudget]);
    const onPressAuto = useCallback(async () => {
        navigateToPeriod();
    }, [navigateToPeriod]);
    const paddingBottom = useMarginBottom('paddingBottom', HEIGHT * 2 + SCREEN_BOTTOM_PADDING, paddingHorizontalStyle);
    return (<AppView withFrame={false}>
      <Animated.ScrollView onScroll={scrollHandler} scrollEventThrottle={16} contentContainerStyle={paddingBottom} scrollIndicatorInsets={SCROLL_INDICATOR_INSETS}>
        <ScreenInfoHeader title={strings.title} subTitle={strings.description}/>

        <View style={styles.contentContainer}>
          <SetUpBudgetsContentHeader />
        </View>
      </Animated.ScrollView>
      <ButtonGroup type="floating" isInTab={isInTab}>
        <Button brandReversed title="Create own budget" onPress={onPressOwn}/>
        <Button brand title="Auto create budget" onPress={onPressAuto}/>
      </ButtonGroup>
    </AppView>);
};
const SetUpBudgetScreen = withAnimatedNavigationHeader(SetUpBudgetScreenContent, ({ colors }) => ({
    pageTitle: 'Set up budget',
    headerColorOutputRange: [
        colors.background.dark,
        colors.cards.onDark,
    ],
}));
const styles = StyleSheet.create({
    contentContainer: {
        paddingHorizontal: rem(16),
    },
});
export default SetUpBudgetScreen;
